import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  TuiAlertService,
  TuiButton,
  TuiHint,
  TuiLoader,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiInputDateRangeModule,
  TuiInputModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/legacy';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiDayRange } from '@taiga-ui/cdk';
import { TuiSearch } from '@taiga-ui/layout';
import * as XLSX from 'xlsx';
import { Subscription } from 'rxjs';
import { ReportData, ReportsService } from '../../../core/services/reports.service';
import { ClientArchivedBadgeComponent } from '../../../shared/components/client-archived-badge/client-archived-badge.component';
import { reportArchivedStatus } from '../report-export.util';

/**
 * Walk-in Clients Report.
 *
 * Modeled after the Non-Registered Patients Report (same toolbar shape and
 * Excel export layout) but each row represents a single walk-in
 * ProcedureRecord (manual client name, no Client document, no shift
 * charges). Reuses the shared `ReportData` shape — `clientId` is null and
 * `shiftCharges` is always 0 for walk-in rows.
 */
@Component({
  selector: 'app-walk-in-clients-report',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterLink,
    TuiTextfield,
    TuiButton,
    TuiHint,
    TuiLoader,
    TuiInputModule,
    TuiInputDateRangeModule,
    TuiTextfieldControllerModule,
    TuiTable,
    TuiSearch,
    ClientArchivedBadgeComponent,
  ],
  templateUrl: './walk-in-clients-report.component.html',
  styleUrl: './walk-in-clients-report.component.scss',
  providers: [DatePipe],
})
export class WalkInClientsReportComponent implements OnInit, OnDestroy {
  private reportsService = inject(ReportsService);
  private alerts = inject(TuiAlertService);
  private datePipe = inject(DatePipe);

  reportData: ReportData[] = [];
  filteredData: ReportData[] = [];
  loading = false;

  rangeFilterTypes: string[] = ['Monthly', 'Custom Range'];
  rangeFilterControl = new FormControl<string>('Monthly');

  searchControl = new FormControl('');
  monthControl = new FormControl<number>(new Date().getMonth());
  yearControl = new FormControl<number>(new Date().getFullYear());
  customRangeControl = new FormControl<TuiDayRange | null>(null);
  selectedMonth: Date | null = null;
  selectedDateRange: { startDate: Date; endDate: Date } | null = null;

  months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];

  years: number[] = [];

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  currentPage = 1;
  pageSize = 100;
  totalRecords = 0;
  totalPages = 0;

  totalAdditionalCharges = 0;
  totalShiftCharges = 0;
  grandTotal = 0;

  private reportSub: Subscription | null = null;

  ngOnInit(): void {
    this.generateYears();

    const today = new Date();
    this.selectedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());

    this.setupRangeFilterTypeListener();
    this.loadReportData();
  }

  ngOnDestroy(): void {
    if (this.reportSub) {
      this.reportSub.unsubscribe();
      this.reportSub = null;
    }
  }

  applyFilters(): void {
    const filterType = this.rangeFilterControl.value;

    if (filterType === 'Monthly') {
      const month = this.monthControl.value;
      const year = this.yearControl.value;
      if (month !== null && year !== null) {
        this.selectedMonth = new Date(year, month, 1);
        this.selectedDateRange = null;
      }
    } else if (filterType === 'Custom Range' && this.customRangeControl.value) {
      const value = this.customRangeControl.value;
      const from = value.from;
      const to = value.to;
      if (from && to) {
        this.selectedDateRange = {
          startDate: new Date(from.year, from.month, from.day),
          endDate: new Date(to.year, to.month, to.day),
        };
        this.selectedMonth = null;
      }
    }

    this.currentPage = 1;
    this.loadReportData();
  }

  setupRangeFilterTypeListener(): void {
    this.rangeFilterControl.valueChanges.subscribe((value) => {
      if (value === 'Monthly') {
        const today = new Date();
        this.monthControl.setValue(today.getMonth());
        this.yearControl.setValue(today.getFullYear());
        this.customRangeControl.setValue(null);
      } else if (value === 'Custom Range') {
        this.customRangeControl.setValue(null);
      }
    });
  }

  loadReportData(): void {
    this.loading = true;

    let filters: any = {
      searchText: this.searchControl.value || '',
    };

    if (this.rangeFilterControl.value === 'Monthly' && this.selectedMonth) {
      filters.month = this.selectedMonth.getMonth() + 1;
      filters.year = this.selectedMonth.getFullYear();
    } else if (
      this.rangeFilterControl.value === 'Custom Range' &&
      this.selectedDateRange
    ) {
      filters.startDate = this.selectedDateRange.startDate.toISOString();
      filters.endDate = this.selectedDateRange.endDate.toISOString();
    }

    const payload = {
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
      filters,
    };

    if (this.reportSub) {
      try {
        this.reportSub.unsubscribe();
      } catch {
        // ignore
      }
      this.reportSub = null;
    }

    this.reportSub = this.reportsService.getWalkInClientsReport(payload).subscribe({
      next: (response: any) => {
        if (response?.success && response?.data) {
          this.reportData = (response.data.reportData || []).map((item: any) => ({
            ...item,
            invoiceDate: new Date(item.invoiceDate),
          }));
          this.filteredData = this.reportData;

          this.totalShiftCharges = response.data.totals?.totalShiftCharges || 0;
          this.totalAdditionalCharges =
            response.data.totals?.totalAdditionalCharges || 0;
          this.grandTotal = response.data.totals?.grandTotal || 0;

          this.totalRecords = response.data.pagination?.total || 0;
          this.totalPages = response.data.pagination?.totalPages || 0;
        } else {
          this.resetReport();
        }
        this.loading = false;
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
      error: (error) => {
        console.error('Error loading walk-in report:', error);
        this.loading = false;
        this.alerts
          .open('Error loading walk-in clients report', { appearance: 'error' })
          .subscribe();
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
    });
  }

  private resetReport(): void {
    this.reportData = [];
    this.filteredData = [];
    this.totalShiftCharges = 0;
    this.totalAdditionalCharges = 0;
    this.grandTotal = 0;
    this.totalRecords = 0;
    this.totalPages = 0;
  }

  clearFilters(): void {
    this.searchControl.setValue('');
    this.rangeFilterControl.setValue('Monthly');

    const today = new Date();
    this.selectedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());
    this.customRangeControl.setValue(null);
    this.selectedDateRange = null;
    this.currentPage = 1;
    this.loadReportData();
  }

  exportToExcel(): void {
    let dateRangeText = '';
    if (this.rangeFilterControl.value === 'Monthly' && this.selectedMonth) {
      dateRangeText = this.datePipe.transform(this.selectedMonth, 'MMMM yyyy') || '';
    } else if (
      this.rangeFilterControl.value === 'Custom Range' &&
      this.selectedDateRange
    ) {
      const startStr = this.datePipe.transform(
        this.selectedDateRange.startDate,
        'MM/dd/yyyy',
      );
      const endStr = this.datePipe.transform(
        this.selectedDateRange.endDate,
        'MM/dd/yyyy',
      );
      dateRangeText = `${startStr} - ${endStr}`;
    } else {
      const today = new Date();
      dateRangeText = this.datePipe.transform(today, 'MMMM yyyy') || '';
    }
    const generated = this.datePipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');

    const data = this.filteredData.map((item) => ({
      'Service Date': this.datePipe.transform(item.invoiceDate, 'MM/dd/yyyy'),
      'Invoice Number': item.invoiceNumber,
      Status: reportArchivedStatus(undefined, item.recordArchived),
      'Client Name': item.clientName,
      'Contact Number': item.contactNumber || '',
      Procedures: (item.procedures || []).join(', '),
      'Nurse Name': item.nurseName || '',
      'Doctor Name': item.doctorName || '',
      Total: (item.total || 0).toFixed(2),
    }));

    data.push({
      'Service Date': '',
      'Invoice Number': '',
      Status: '',
      'Client Name': 'TOTALS',
      'Contact Number': '',
      Procedures: '',
      'Nurse Name': '',
      'Doctor Name': '',
      Total: this.grandTotal.toFixed(2),
    });

    const worksheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A6' });

    worksheet['!cols'] = [
      { wch: 14 }, // Service Date
      { wch: 20 }, // Invoice Number
      { wch: 12 }, // Status
      { wch: 28 }, // Client Name
      { wch: 18 }, // Contact Number
      { wch: 30 }, // Procedures
      { wch: 22 }, // Nurse Name
      { wch: 22 }, // Doctor Name
      { wch: 14 }, // Total
    ];

    const headerRows = [
      ['ROYAL HOSPITAL - HOME NURSING CARE'],
      ['Walk-in Clients Revenue Report'],
      [`Date Range: ${dateRangeText}`],
      [`Generated: ${generated}`],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, headerRows, { origin: 'A1' });

    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 8 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 8 } },
    ];

    const setCellStyle = (cell: string, bold: boolean, size: number) => {
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold, sz: size },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    };

    setCellStyle('A1', true, 16);
    setCellStyle('A2', true, 14);
    setCellStyle('A3', true, 12);
    setCellStyle('A4', true, 12);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Walk-in Clients');
    XLSX.writeFile(workbook, `Walk_In_Clients_Report_${Date.now()}.xlsx`);
  }
}
